import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import { RouteError } from '@/components/composed/RouteError';
import { NotFound } from '@/components/composed/NotFound';
import { useQuery } from '@tanstack/react-query';

import { usePageMeta } from '@/app/shell/use-page-meta';
import { canAdministerUsers, redirectPathForRole } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import { AccountPopulationReport, reportableOrgsQuery } from '@/features/users';

/*
 * The Administrator-gated account-population report page (F02 R39), SITED WITH
 * SYSTEM CONFIGURATION under `/admin/account-population` — the admin area,
 * alongside user administration, not with billing operations (AC-02d2e999, the
 * placement half; asserted structurally in the guard test).
 *
 *   - AC-02d2e999 — the WAY IN is withheld from anyone other than an
 *     Administrator: a `beforeLoad` guard reads the derived session from the
 *     auth store IMPERATIVELY (guards run outside React, per AGENTS.md) and,
 *     when the person does NOT hold the is_administrator privilege
 *     (`canAdministerUsers` is false), throws a redirect that RETURNS THEM TO
 *     THEIR OWN HOME PAGE (`redirectPathForRole`). The server gates the report
 *     route and refuses regardless (the rule); this withholds the page (the
 *     courtesy half). The gate is the PRIVILEGE, not a role, so an admin-ROLE
 *     account without the switch is bounced too.
 */
export const Route = createFileRoute('/_auth/admin/account-population')({
  /*
   * The is_administrator privilege guard (AC-02d2e999, the courtesy half).
   * Same pattern as `admin.users`: read the derived session from the auth store
   * IMPERATIVELY (getState(), never a hook — guards run outside React), and a
   * person who does not hold the privilege is RETURNED TO THEIR OWN HOME PAGE
   * via the central redirect map. The gate is the privilege, not a role. The
   * server (task 1) is the authority and refuses the report route regardless —
   * this only withholds the page.
   */
  beforeLoad: () => {
    const session = useAuthStore.getState().session;
    if (!canAdministerUsers(session)) {
      throw redirect({
        to: redirectPathForRole(session?.role ?? 'client'),
      });
    }
  },
  /*
   * Prime the scope-respecting org list the selector offers (AC-832c0d75), so
   * the first paint has the organizations in hand. The report itself is read
   * per selected org by the component (keyed separately so each org is cached
   * on its own).
   */
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(reportableOrgsQuery()),
  component: AccountPopulationPage,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

function AccountPopulationPage() {
  usePageMeta({ title: 'Account population' });

  // The organizations the report may be read for — Kelly's own org and any
  // named OTHER org the Administrator's scope permits, read SEPARATELY
  // (AC-832c0d75). The list is the server's (scope-respecting); the selector
  // offers exactly these and never widens them. Each option's value is the
  // opaque org id — switching to a named OTHER org re-reads the report for that
  // id, with no name in the URL/telemetry (AGENTS.md: ids only).
  const { data: orgs } = useQuery(reportableOrgsQuery());
  const options = orgs ?? [];
  const [organizationId, setOrganizationId] = useState<string>('');
  // Default to the first permitted org once the list resolves (without clobbering
  // an operator's later choice).
  const selected = organizationId || options[0]?.id || '';

  return (
    <Page>
      <PageHeader
        title="Account population"
        description="Accounts counted by role, privilege and dormancy, for a selected organization."
      />
      <Section className="gap-6">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="account-population-org"
            className="text-sm font-medium"
          >
            Organization
          </label>
          <select
            id="account-population-org"
            className="w-fit rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={selected}
            onChange={(e) => setOrganizationId(e.target.value)}
          >
            {options.map((org) => (
              <option key={org.id} value={org.id}>
                {org.label}
              </option>
            ))}
          </select>
        </div>
        {/* Scoped to the ONE selected org, by its opaque id (AC-832c0d75). */}
        {selected ? (
          <AccountPopulationReport organizationId={selected} />
        ) : null}
      </Section>
    </Page>
  );
}
